import { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchQueuedJobs } from '../store/slices/jobSlice';
import { QUEUED_PAGE_SIZE } from '../services/jobApi';

/**
 * The queued jobs, a page of a hundred at a time, for every desktop job view.
 *
 * <p>The Board's Queued column, the split view's sidebar and the planner's active list
 * all draw their queued jobs from GET /jobs/queued and never from the month payload,
 * so a queued job is never on a screen twice and the count is the count of all of
 * them. This hook is that rule in one place: it owns the page, asks the server, and
 * hands back the page and the total. The settled search term is passed in; each view
 * also narrows the page locally with {@code filterByNameOrRego} on every keystroke.
 *
 * @param {{ enabled?: boolean, onTv?: boolean, q?: string|null }} options
 *        enabled — false on the phone, which has its own list and never draws this
 *        onTv    — TV Mode: ask only for the jobs picked for the wall. Sent only when
 *                  true; a change in or out of TV Mode starts again from page one.
 *        q       — the SETTLED, VALIDATED search term (utils/searchTerm), or null for
 *                  none. The caller debounces and checks it; this sends it. A new term
 *                  starts again from page one.
 */
export default function useQueuedJobs({ enabled = true, onTv = false, q = null } = {}) {
    const dispatch = useDispatch();
    const queued = useSelector((state) => state.jobs.queued);
    const [page, setPage] = useState(0);

    useEffect(() => { setPage(0); }, [onTv, q]);

    const load = useCallback((bustCache = false) => {
        if (!enabled) return;
        dispatch(fetchQueuedJobs({
            page,
            size: QUEUED_PAGE_SIZE,
            onTv: onTv ? true : null,
            q: q ?? '',
            ...(bustCache ? { bustCache: true } : {}),
        }));
    }, [dispatch, enabled, page, onTv, q]);

    useEffect(() => { load(); }, [load]);

    // Stable, so a view can put it in a useCallback's deps without refetching on
    // every render.
    const reload = useCallback(() => load(true), [load]);

    return {
        jobs: queued.jobs,
        total: queued.total,
        page: queued.page,
        totalPages: queued.totalPages,
        loading: queued.loading,
        /** The server's refusal, verbatim, if it ever disagrees with the client's check. */
        error: queued.error,
        size: QUEUED_PAGE_SIZE,
        setPage,
        /** Ask again, past any cache — what the Refresh button and the TV poll call. */
        reload,
    };
}
